import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, CheckoutProductCard, Dialog } from "@/components";
import { toBarcodeLabel, useCurrentStore, usePrintLabels } from "@/hooks";
import { z } from "../../constants/zod";

type PrintBarcodesDialogProps = {
	shippingProducts: ShippingProduct[];
	open: boolean;
	onClose: () => void;
};

// The lines sit under a key: react-hook-form's form value has to be an
// object, not a bare array.
const formSchema = z.object({
	lines: z
		.array(
			z.object({
				id: z.string(),
				quantity: z.number(),
				product: z.custom<Product>(),
			}),
		)
		.min(1),
});

type FormType = z.infer<typeof formSchema>;

const PrintBarcodesDialog = ({
	shippingProducts,
	open,
	onClose,
}: PrintBarcodesDialogProps) => {
	const { t } = useTranslation();
	const store = useCurrentStore();
	const { control, reset, getValues, watch } = useForm<FormType>({
		resolver: zodResolver(formSchema),
	});

	const { mutateAsync: printLabels, isPending: isPrintingSingle } =
		usePrintLabels();

	const onPrintSingle = ({
		product,
		quantity,
	}: {
		product: Product;
		quantity: number;
	}) => {
		if (!store) {
			return;
		}

		printLabels([toBarcodeLabel(product, store, quantity)]);
	};

	// The whole batch goes down in one call. The Electron build looped here and
	// slept `quantity * 1000` ms between products to keep from overrunning the
	// printer; that pacing now lives next to the serial write, in Rust.
	const { mutate: onPrint, isPending } = useMutation({
		mutationFn: async () => {
			if (!store) {
				return;
			}

			await printLabels(
				(getValues("lines") ?? []).map((shippingProduct) =>
					toBarcodeLabel(
						shippingProduct.product,
						store,
						shippingProduct.quantity,
					),
				),
			);
		},
		onSuccess: () => onClose(),
	});

	useEffect(() => {
		reset({ lines: shippingProducts });
	}, [reset, shippingProducts]);

	return (
		<Dialog open={open} onClose={onClose}>
			<div className="flex flex-col items-stretch gap-8">
				<h2>{t("shipping.printBarcodes.title")}</h2>

				<div className="flex flex-col items-stretch gap-4">
					{(watch("lines") ?? []).map((shippingProduct, index) => (
						<div
							key={shippingProduct.id}
							className="flex flex-row items-center gap-4"
						>
							<div className="flex-1">
								<CheckoutProductCard
									withoutPrice
									productId={shippingProduct.product.id}
									control={control}
									quantity={shippingProduct.quantity}
									quantityInputName={`lines.${index}.quantity`}
								/>
							</div>
							<Button
								variant="outline"
								onClick={() =>
									onPrintSingle({
										product: shippingProduct.product,
										quantity: shippingProduct.quantity,
									})
								}
								disabled={isPrintingSingle}
							>
								{t("global.print")}
							</Button>
						</div>
					))}
				</div>

				<div className="flex flex-row items-center justify-end gap-4">
					<Button variant="ghost" onClick={onClose}>
						{t("global.cancel")}
					</Button>
					<Button onClick={() => onPrint()} disabled={isPending}>
						{t("global.print")}
					</Button>
				</div>
			</div>
		</Dialog>
	);
};

export default PrintBarcodesDialog;
